import { CheckDescriptors } from '../types';

export const STOREFRONT_SETTINGS = [
    {
        id: 'storefront.general.name',
        area: 'storefront.general',
        label: 'Storefront Name',
    },
    {
        id: 'storefront.general.domain',
        area: 'storefront.general',
        label: 'Storefront Domain',
        description:
            "Picks a domain from the organization's own custom domains, when more than one is available. Otherwise the storefront uses the default store.mspcart.com domain.",
    },
    {
        id: 'storefront.general.customDomains',
        area: 'storefront.general',
        label: 'Custom Domains',
        description:
            "Connect domains the organization owns (like store.yourcompany.com): add a domain, create the DNS records it shows, and verification plus the HTTPS certificate are handled automatically. Verified domains appear in the Storefront Domain dropdown and are shared by all of the organization's storefronts; each can name a default storefront served at the bare domain. Requires the custom domains entitlement.",
    },
    {
        id: 'storefront.general.subUrl',
        area: 'storefront.general',
        label: 'Sub URL',
        description:
            'The path segment the storefront is served from. Changing it breaks existing links to the storefront.',
    },
    {
        id: 'storefront.general.favicon',
        area: 'storefront.general',
        label: 'Favicon',
    },
    {
        id: 'storefront.general.enabled',
        area: 'storefront.general',
        label: 'Storefront Active',
    },

    // Storefront pages editor - global settings shown above the page tabs
    {
        id: 'storefront.pages.maxWidth',
        area: 'storefront.pages',
        label: 'Max Content Width (pixels)',
        description: 'Caps the width of page content across every page on this storefront.',
    },

    // Storefront list (all storefronts)
    {
        id: 'storefront.list.usePreview',
        area: 'storefront.list',
        label: 'Enable Storefront Preview',
        description: 'Changes to storefronts are visible on a preview URL before being published to the main storefront.',
    },

    // Storefront products (all storefronts)
    {
        id: 'storefront.global.products.defaultMarkup',
        area: 'storefront.global.products.settings',
        label: 'Default Markup',
        description: 'Default markup applied to storefronts that do not set their own.',
    },
    {
        id: 'storefront.global.products.defaultMarkupType',
        area: 'storefront.global.products.settings',
        label: 'Default Markup Type',
        description: 'Default markup type applied to storefronts that do not set their own.',
    },
    {
        id: 'storefront.global.products.storefrontVendors',
        area: 'storefront.global.products.settings',
        label: 'Storefront Vendor Filter',
        description:
            "Restricts every storefront's product listings to these vendors, unless a storefront sets its own vendor filter.",
    },
    {
        id: 'storefront.global.products.vendorFilterStrategy',
        area: 'storefront.global.products.settings',
        label: 'Vendor Filter Strategy',
        description:
            'Whether the vendor list above includes only those vendors or excludes them. Used only when a storefront does not set its own filter.',
    },
    {
        id: 'storefront.global.products.showMfpOnProducts',
        area: 'storefront.global.products.settings',
        label: 'Show MFP on Product Details',
        description: 'Displays the Manufacturer Part Number on the storefront Product Details page.',
    },
    {
        id: 'storefront.global.products.showLinkedItemsMode',
        area: 'storefront.global.products.settings',
        label: 'Show Linked Items on Storefronts',
        description:
            "Controls whether products' linked items appear on storefront product pages. Select Products shows them only for products enabled individually.",
    },

    // Header links container
    {
        id: 'storefront.headerLinks.align',
        area: 'storefront.pages.headerLinks',
        label: 'Alignment',
        description: 'Where the custom header links sit relative to the logo and the header actions.',
    },

    // Navigation pods (styling shared by every pod)
    {
        id: 'storefront.navPods.width',
        area: 'storefront.pages.navPods',
        label: 'Width',
        description: 'Applies to every pod so the row stays uniform. Blank leaves the storefront default of 140.',
    },
    {
        id: 'storefront.navPods.height',
        area: 'storefront.pages.navPods',
        label: 'Height',
        description: 'Applies to every pod so the row stays uniform. Blank leaves the storefront default of 180.',
    },
    {
        id: 'storefront.navPods.align',
        area: 'storefront.pages.navPods',
        label: 'Alignment',
        description:
            'Blank uses the storefront default: centered when pods wrap to a new line, left aligned when they scroll.',
    },
    {
        id: 'storefront.navPods.overflow',
        area: 'storefront.pages.navPods',
        label: 'Overflow',
        description: 'Whether a row too wide to fit scrolls with arrows or wraps to the next line.',
    },
    {
        id: 'storefront.navPodImage.width',
        area: 'storefront.pages.navPods.style',
        label: 'Image Width',
        description: 'Applies to every pod\'s artwork. Blank leaves the default 64px square.',
    },
    {
        id: 'storefront.navPodImage.height',
        area: 'storefront.pages.navPods.style',
        label: 'Image Height',
        description: 'Applies to every pod\'s artwork. Blank leaves the default 64px square.',
    },

    // Browse All band (bottom-of-home-page call to action)
    {
        id: 'storefront.browseAll.enabled',
        area: 'storefront.pages.browseAll',
        label: 'Browse All band',
        description: 'Turns the bottom "Browse All" area into a full-width call-to-action band. Off, the shopper gets the classic centered button.',
    },
    {
        id: 'storefront.browseAll.heading',
        area: 'storefront.pages.browseAll',
        label: 'Heading',
        condition: 'Only appears when the Browse All band is on.',
        dependsOn: 'storefront.browseAll.enabled',
    },
    {
        id: 'storefront.browseAll.subheading',
        area: 'storefront.pages.browseAll',
        label: 'Subheading',
        condition: 'Only appears when the Browse All band is on.',
        dependsOn: 'storefront.browseAll.enabled',
    },
    {
        id: 'storefront.browseAll.overlayOpacity',
        area: 'storefront.pages.browseAll',
        label: 'Overlay Opacity',
        description: 'Strength of the tint wash over the background image.',
        condition: 'Only appears when the Browse All band is on.',
        dependsOn: 'storefront.browseAll.enabled',
    },
    {
        id: 'storefront.browseAll.minHeight',
        area: 'storefront.pages.browseAll',
        label: 'Min Height',
        condition: 'Only appears when the Browse All band is on.',
        dependsOn: 'storefront.browseAll.enabled',
    },
    {
        id: 'storefront.browseAll.fixed',
        area: 'storefront.pages.browseAll',
        label: 'Fixed background (parallax)',
        condition: 'Only appears when the Browse All band is on.',
        dependsOn: 'storefront.browseAll.enabled',
    },

    // Header behavior settings (sticky, search row, bar height) — a toolbar flyout on the
    // Header & Footer tab
    {
        id: 'storefront.header.sticky',
        area: 'storefront.pages.headerSettings',
        label: 'Sticky Header',
        description: 'When on, the header bar stays fixed at the top of the page and only the content below it scrolls.',
    },
    {
        id: 'storefront.header.searchRow',
        area: 'storefront.pages.headerSettings',
        label: 'Search bar as a row below the header',
        description: 'Moves the header search out of the top row into its own full-width row beneath the header. Needs the header search enabled.',
    },
    {
        id: 'storefront.header.searchRowSticky',
        area: 'storefront.pages.headerSettings',
        label: 'Sticky search row',
        description: 'Pins the search row on scroll, independent of the header.',
        condition: 'Only appears when Search bar as a row below the header is on.',
        dependsOn: 'storefront.header.searchRow',
    },
    {
        id: 'storefront.header.barHeight',
        area: 'storefront.pages.headerSettings',
        label: 'Header bar height',
        description: 'Empty is automatic: the bar grows to fit the logo, links and search. Minimum 24.',
    },

    // Footer content
    {
        id: 'storefront.footer.text',
        area: 'storefront.pages.headerFooter',
        label: 'Footer Text',
    },
    {
        id: 'storefront.footer.contactDetails',
        area: 'storefront.pages.headerFooter',
        label: 'Footer Contact Details',
    },

    // Section options (Home tab, Edit Mode)
    {
        id: 'storefront.sections.ignoreGlobalSections',
        area: 'storefront.pages.home',
        label: 'Ignore Global Sections',
        description:
            "Skips the organization-wide sections on this storefront's home page. Behind the Section Options button, in Edit Mode.",
    },
    // Header & footer links
    {
        /*
         * Unlike Ignore Global Sections and Ignore Global Banners, nothing on the live page editor
         * sets this one yet - the Header/Footer links editors only read it. The Storefront Details
         * panel (opened from a product, bundle or saved search) is the only place it can be changed
         * today, so a link here may resolve to the right screen family without highlighting anything.
         */
        id: 'storefront.links.ignoreGlobalLinks',
        area: 'storefront.pages.headerFooter',
        label: 'Ignore Global Links',
        description: 'Skips the organization-wide header and footer links on this storefront.',
        // Not a setting-gate - the field is simply not on the Pages editor at all. dependsOn stays
        // unset; there is no controlling setting to link to, just a different surface to visit.
        condition: 'Only editable from the storefront details panel (opened from a product, bundle or saved search).',
    },

    // Banner carousel (per page, not per slide)
    {
        id: 'storefront.banners.ignoreGlobalBanners',
        area: 'storefront.pages.banners',
        label: 'Ignore Global Banners',
        description:
            "Skips the organization-wide banners on this storefront's home page, showing only the banners set here.",
    },
    {
        id: 'storefront.banners.height',
        area: 'storefront.pages.banners',
        label: 'Height',
        description:
            "Leave empty to use the image's natural height for a single banner; carousels default to 350px.",
    },
    {
        id: 'storefront.banners.rotationSeconds',
        area: 'storefront.pages.banners',
        label: 'Rotation seconds',
        description:
            'Seconds each slide shows before the carousel advances, on every page of the store. Empty = 7 seconds; 0 turns auto-advance off; the live store holds values to a 2 second minimum.',
        condition: 'Only appears when editing the main home page (the value applies store-wide).',
    },
    {
        id: 'storefront.banners.fullWidth',
        area: 'storefront.pages.banners',
        label: 'Full page width',
        description: "Whether the banner spans the full page width or stays within the page's content width.",
    },
    {
        id: 'storefront.banners.imageFit',
        area: 'storefront.pages.banners',
        label: 'Image fit',
        description: 'Whether the hero image letterboxes to fit its box or crops to fill it.',
    },
    {
        id: 'storefront.banners.underHeader',
        area: 'storefront.pages.banners',
        label: 'Under header',
        description:
            'Lets the header float over a full-width banner instead of sitting above it. Desktop only on the live store.',
        condition: 'Only appears when Full page width is on.',
        dependsOn: 'storefront.banners.fullWidth',
    },
    {
        id: 'storefront.banners.overlayBarColor',
        area: 'storefront.pages.banners',
        label: 'Overlay bar color',
        description:
            'Background color of the floating header bar over the banner before the page scrolls. Only applies when Full page width and Under header are both on.',
        condition: 'Only appears when Full page width and Under header are both on.',
        dependsOn: 'storefront.banners.underHeader',
    },
    {
        id: 'storefront.banners.overlayBarShadow',
        area: 'storefront.pages.banners',
        label: 'Overlay bar shadow',
        description:
            'Box shadow under the floating header bar before the page scrolls. Only applies when Full page width and Under header are both on.',
        condition: 'Only appears when Full page width and Under header are both on.',
        dependsOn: 'storefront.banners.underHeader',
    },

    // Storefront integrations (organization-wide)
    {
        id: 'storefront.global.integrations.enableOpportunityCreation',
        area: 'storefront.global.integrations',
        label: 'Enable Opportunity Extension',
        description: 'Creates a new opportunity in your CRM on storefront checkout. Used as the default for storefronts that do not override it.',
    },
    {
        id: 'storefront.global.integrations.enableSalesOrderCreation',
        area: 'storefront.global.integrations',
        label: 'Create Sales Order on Order Placement',
        description: 'Automatically converts the opportunity created on checkout into a sales order in the same CRM. Can be overridden at the storefront level.',
        condition: 'Only appears when Enable Opportunity Extension is on and an opportunity extension is selected.',
        dependsOn: 'storefront.global.integrations.enableOpportunityCreation',
    },
    {
        id: 'storefront.global.integrations.customerLookupExtension',
        area: 'storefront.global.integrations.customers',
        label: 'Customer Lookup Extension',
        description: 'Used during customer signup to search the CRM for a related record. Used as the default for storefronts that do not override it.',
    },
    {
        id: 'storefront.global.integrations.locationLookupEnabled',
        area: 'storefront.global.integrations.locations',
        label: 'Enable Location Lookup',
        description: 'Lets customers pick shipping and billing addresses from CRM locations. The customer must be linked to an active CRM customer.',
    },
    {
        id: 'storefront.global.integrations.locationLookupExtension',
        area: 'storefront.global.integrations.locations',
        label: 'Location Lookup Extension',
        description: 'Used as the default for storefronts that do not override it.',
    },

    // Custom SMTP (organization-wide)
    {
        id: 'storefront.smtp.enabled',
        area: 'storefront.global.integrations.email',
        label: 'Enable Custom SMTP',
        description: 'Send storefront email through your own mail server instead of the platform default.',
    },
    {
        id: 'storefront.smtp.host',
        area: 'storefront.global.integrations.email',
        label: 'Host',
    },
    {
        id: 'storefront.smtp.port',
        area: 'storefront.global.integrations.email',
        label: 'Port',
    },
    {
        id: 'storefront.smtp.useTls',
        area: 'storefront.global.integrations.email',
        label: 'Use TLS',
    },
    {
        id: 'storefront.smtp.username',
        area: 'storefront.global.integrations.email',
        label: 'Username',
    },
    {
        id: 'storefront.smtp.password',
        area: 'storefront.global.integrations.email',
        label: 'Password',
        description:
            'Write-only. Once saved it lives only in Key Vault, so this field stays blank; leave it blank to keep the current password.',
    },

    // Per-storefront authentication
    {
        id: 'storefront.authentication.allowAnonymousAccess',
        area: 'storefront.authentication.login',
        label: 'Allow Anonymous Browsing',
        description:
            'Lets guests without known contact details browse the storefront. Guests cannot add to cart, view orders, or place orders.',
    },
    {
        id: 'storefront.authentication.anonymousShowPricing',
        area: 'storefront.authentication.login',
        label: 'Show Pricing For Anonymous Users',
        description: 'Only applies when Allow Anonymous Browsing is enabled.',
    },
    {
        id: 'storefront.authentication.loginMode',
        area: 'storefront.authentication.login',
        label: 'When a shopper opens the storefront',
        description: 'Whether the storefront opens on the sign-in page or straight to the home page as a guest.',
    },
    {
        id: 'storefront.authentication.loginMethod',
        area: 'storefront.authentication.login',
        label: 'How shoppers finish signing in',
        description: 'Magic link, a code typed on the page, or opening the link in a new tab.',
    },
    {
        id: 'storefront.authentication.allowSignup',
        area: 'storefront.authentication.signup',
        label: 'Allow Signup',
        description:
            'Lets users create an account. With a customer lookup extension configured, signups are matched to a contact in your system.',
    },
    {
        id: 'storefront.authentication.unresolvedSignupPolicy',
        area: 'storefront.authentication.signup',
        label: 'When someone signs up and we cannot match them to a contact',
        description:
            'Let them in straight away, or hold them for approval. With no lookup extension configured nobody is recognised, so holding queues every signup.',
    },
    {
        id: 'storefront.authentication.autoApproveDomains',
        area: 'storefront.authentication.signup',
        label: 'Approve these domains automatically',
        description:
            'Comma separated, whole-domain match: acme.com admits ada@acme.com but not ada@mail.acme.com.',
        condition: 'Only appears when signups that cannot be matched to a contact are set to Hold Them For Approval.',
        dependsOn: 'storefront.authentication.unresolvedSignupPolicy',
    },
    {
        id: 'storefront.authentication.signupTermsAndConditions',
        area: 'storefront.authentication.signup',
        label: 'Signup Terms and Conditions',
    },
    {
        id: 'storefront.authentication.signupNotificationEmail',
        area: 'storefront.authentication.signup',
        label: 'Notify these addresses of signups',
        description: 'Comma separated, sent to staff rather than the shopper. Leave empty to send nothing.',
    },
    {
        id: 'storefront.authentication.signupEmailSubject',
        area: 'storefront.authentication.signup',
        label: 'Signup subject',
        description: 'Leave empty for the default.',
        condition: 'Only appears when at least one address is set to notify of signups.',
        dependsOn: 'storefront.authentication.signupNotificationEmail',
    },
    {
        id: 'storefront.authentication.signupEmailBody',
        area: 'storefront.authentication.signup',
        label: 'Signup body',
        description: 'Leave empty for the default. Sent to the notification addresses when someone signs up.',
        condition: 'Only appears when at least one address is set to notify of signups.',
        dependsOn: 'storefront.authentication.signupNotificationEmail',
    },
    {
        id: 'storefront.authentication.signupApprovalEmailSubject',
        area: 'storefront.authentication.signup',
        label: 'Awaiting approval subject',
        description: 'Leave empty for the default.',
        condition: 'Only appears when at least one address is set to notify of signups and unmatched signups are set to Hold Them For Approval.',
        dependsOn: 'storefront.authentication.unresolvedSignupPolicy',
    },
    {
        id: 'storefront.authentication.signupApprovalEmailBody',
        area: 'storefront.authentication.signup',
        label: 'Awaiting approval body',
        description: 'Leave empty for the default. Sent when a signup is held for approval.',
        condition: 'Only appears when at least one address is set to notify of signups and unmatched signups are set to Hold Them For Approval.',
        dependsOn: 'storefront.authentication.unresolvedSignupPolicy',
    },
    {
        id: 'storefront.authentication.signupApprovedEmailSubject',
        area: 'storefront.authentication.signup',
        label: 'Approved email subject',
        description: 'Leave empty for the default.',
        condition: 'Only appears when unmatched signups are set to Hold Them For Approval.',
        dependsOn: 'storefront.authentication.unresolvedSignupPolicy',
    },
    {
        id: 'storefront.authentication.signupApprovedEmailBody',
        area: 'storefront.authentication.signup',
        label: 'Approved email body',
        description: 'Sent to the shopper when you approve a held signup.',
        condition: 'Only appears when unmatched signups are set to Hold Them For Approval.',
        dependsOn: 'storefront.authentication.unresolvedSignupPolicy',
    },

    // Per-storefront integrations
    {
        id: 'storefront.integrations.opportunityExtension',
        area: 'storefront.integrations.opportunities',
        label: 'Opportunity Extension',
        description: 'CRM extension that receives opportunities created from this storefront checkouts. Overrides the all-storefronts default.',
    },
    {
        id: 'storefront.integrations.disableOpportunityCreation',
        area: 'storefront.integrations.opportunities',
        label: 'Disable Opportunity Creation',
        description: 'Stops checkout opportunity creation for this storefront even when enabled globally.',
    },
    {
        id: 'storefront.integrations.disableSalesOrderCreation',
        area: 'storefront.integrations.opportunities',
        label: 'Disable Sales Order Creation',
        condition: 'Under Show Details, when sales order creation is enabled globally and opportunity creation is not disabled.',
        dependsOn: 'storefront.integrations.disableOpportunityCreation',
    },
    {
        id: 'storefront.integrations.quoteRequestExtension',
        area: 'storefront.integrations.opportunities',
        label: 'Quote Request Opportunity Extension',
        description: 'CRM extension that receives opportunities created from quote requests on this storefront.',
    },
    {
        id: 'storefront.integrations.disableOpportunityCreationQuoteRequest',
        area: 'storefront.integrations.opportunities',
        label: 'Disable Quote Request Opportunity Creation',
    },
    {
        id: 'storefront.integrations.customerLookupExtension',
        area: 'storefront.integrations.customers',
        label: 'Customer Lookup Extension',
        description: 'Used during customer signup to search the CRM for a related record. Overrides the global setting.',
    },
    {
        id: 'storefront.integrations.locationLookupExtension',
        area: 'storefront.integrations.locations',
        label: 'Location Lookup Extension',
        description: 'Lets customers pick shipping and billing addresses from CRM locations. Overrides the global setting.',
    },
    {
        id: 'storefront.integrations.taxExtension',
        area: 'storefront.integrations.tax',
        label: 'Tax Extension',
        description:
            'Calculates tax on checkout totals for this storefront through the extension instead of a flat tax code. Overrides the global setting.',
    },

    // Per-storefront ordering and carts
    {
        id: 'storefront.ordering.orderEmail',
        area: 'storefront.ordering.email',
        label: 'Order Confirmation BCC',
        description: 'Overrides the organization-wide setting for this storefront.',
    },
    {
        id: 'storefront.ordering.customerEmailSubject',
        area: 'storefront.ordering.email',
        label: 'Order Confirmation Email Subject',
        description: 'Overrides the organization-wide setting for this storefront.',
    },
    {
        id: 'storefront.ordering.allowQuoteRequest',
        area: 'storefront.ordering.quotes',
        label: 'Allow Quote Request',
        description: "Lets customers submit a quote request for their cart from the storefront.",
    },
    {
        id: 'storefront.ordering.quoteRequestContact',
        area: 'storefront.ordering.quotes',
        label: 'Contact for Quote Requests',
        description: 'The sales rep assigned to quote requests submitted on this storefront.',
        /*
         * Rendered unconditionally right below the toggle - not a render-gate the "couldn't find it"
         * toast would ever need - but functionally inert until Allow Quote Request is on, which is
         * worth saying either way. "Only applies", not "only appears", since the field is always on
         * the page.
         */
        condition: 'Only applies when Allow Quote Request is on.',
        dependsOn: 'storefront.ordering.allowQuoteRequest',
    },
    {
        id: 'storefront.ordering.quoteRequestBcc',
        area: 'storefront.ordering.quotes',
        label: 'Quote Request BCC',
        description:
            'Additional addresses that receive a copy of the sales rep quote request notification, for a sales manager or shared inbox. Separate multiple addresses with commas.',
        dependsOn: 'storefront.ordering.allowQuoteRequest',
    },
    {
        id: 'storefront.ordering.allowCartSharing',
        area: 'storefront.ordering.quotes',
        label: 'Allow Cart Sharing',
        description: 'Lets customers share their cart with someone else by email.',
    },

    {
        id: 'storefront.ordering.preparedCartEmailSubject',
        area: 'storefront.ordering.email',
        label: 'Prepared Cart Email Subject',
        description:
            'Subject line for the email sent to a customer when a sales rep shares a prepared cart with them. Supports tags.',
    },
    {
        id: 'storefront.ordering.preparedCartCustomerCommentSubject',
        area: 'storefront.ordering.email',
        label: 'Customer Comment Subject',
        description:
            'Subject line for the email sent to the sales rep when a customer leaves a comment on their prepared cart. Supports tags.',
    },
    {
        id: 'storefront.ordering.customerQuoteRequestEmailSubject',
        area: 'storefront.ordering.email',
        label: 'Customer Quote Request Email Subject',
        description:
            'Subject line for the email sent to a customer confirming their quote request was received. Supports tags.',
    },
    {
        id: 'storefront.ordering.salesRepQuoteRequestEmailSubject',
        area: 'storefront.ordering.email',
        label: 'Quote Request Sales Rep Email Subject',
        description:
            'Subject line for the email sent to the sales rep when a customer submits a quote request. Supports tags.',
    },
    {
        id: 'storefront.ordering.sharedCartEmailSubject',
        area: 'storefront.ordering.email',
        label: 'Shared Cart Email Subject',
        description:
            'Subject line for the email sent when a customer shares their cart with someone else. Supports tags.',
    },

    {
        id: 'storefront.ordering.preparedCartEmailBody',
        area: 'storefront.ordering.email',
        label: 'Prepared Cart Email Template',
        description:
            'HTML template for the email sent to a customer when a sales rep shares a prepared cart with them. Supports tags and opens with the stock template already filled in.',
    },
    {
        id: 'storefront.ordering.preparedCartCustomerCommentBody',
        area: 'storefront.ordering.email',
        label: 'Customer Comment Email Template',
        description:
            'HTML template for the email sent to the sales rep when a customer leaves a comment on their prepared cart. Supports tags and opens with the stock template already filled in.',
    },
    {
        id: 'storefront.ordering.customerQuoteRequestEmailBody',
        area: 'storefront.ordering.email',
        label: 'Customer Quote Request Email Template',
        description:
            'HTML template for the email sent to a customer confirming their quote request was received. Supports tags and opens with the stock template already filled in.',
    },
    {
        id: 'storefront.ordering.salesRepQuoteRequestEmailBody',
        area: 'storefront.ordering.email',
        label: 'Quote Request Sales Rep Email Template',
        description:
            'HTML template for the email sent to the sales rep when a customer submits a quote request. Supports tags and opens with the stock template already filled in.',
    },
    {
        id: 'storefront.ordering.sharedCartEmailBody',
        area: 'storefront.ordering.email',
        label: 'Shared Cart Email Template',
        description:
            'HTML template for the email sent when a customer shares their cart with someone else. Supports tags and opens with the stock template already filled in.',
    },

    // Organization-wide ordering and carts
    {
        id: 'storefront.global.ordering.orderEmail',
        area: 'storefront.global.ordering.email',
        label: 'Order Confirmation BCC',
        description: 'Default BCC address for order confirmation emails, used by storefronts that do not set their own.',
    },
    {
        id: 'storefront.global.ordering.orderFailureEmail',
        area: 'storefront.global.ordering.email',
        label: 'Order Failure Email',
        description: 'Notified when there is an error processing a customer order event.',
    },
    {
        id: 'storefront.global.ordering.customerEmailSubject',
        area: 'storefront.global.ordering.email',
        label: 'Order Confirmation Email Subject',
        description: 'Subject line for order confirmation emails. Supports tags.',
    },
    {
        id: 'storefront.global.ordering.enableSimpleTaxCalculation',
        area: 'storefront.global.ordering.payments',
        label: 'Enable Simple Tax Calculation',
        description:
            'Applies a single flat tax rate per storefront instead of calculating tax by customer location, product type, or shipping address.',
    },
    {
        id: 'storefront.global.ordering.collectTax',
        area: 'storefront.global.ordering.payments',
        label: 'Collect Tax',
        description:
            'Turns on tax collection for storefront orders. Also required, along with Enable Simple Tax Calculation, for Default Tax Code to become selectable.',
    },
    {
        id: 'storefront.global.ordering.defaultTaxCode',
        area: 'storefront.global.ordering.payments',
        label: 'Default Tax Code',
        description: 'Flat tax code applied when a storefront does not set its own.',
        condition: 'Only appears when Collect Tax and Enable Simple Tax Calculation are both on.',
        dependsOn: 'storefront.global.ordering.collectTax',
    },
    {
        id: 'storefront.global.ordering.defaultShippingCharge',
        area: 'storefront.global.ordering.shipping',
        label: 'Default Shipping Charge',
        description: 'Default flat shipping charge, used by storefronts that do not set their own.',
    },

    // Stripe configuration (organization-wide, opened from the Payments & Billing tab)
    {
        id: 'storefront.global.ordering.stripeCreateInvoice',
        area: 'storefront.global.ordering.payments.stripe',
        label: 'Create Invoice',
        description: 'Generates a post-purchase Stripe invoice for each storefront order.',
    },
    {
        id: 'storefront.global.ordering.stripeInvoiceDescription',
        area: 'storefront.global.ordering.payments.stripe',
        label: 'Invoice Description',
        description: 'Supports tags.',
        condition: 'Only appears when Create Invoice is on.',
        dependsOn: 'storefront.global.ordering.stripeCreateInvoice',
    },
    {
        id: 'storefront.global.ordering.stripeInvoiceFooter',
        area: 'storefront.global.ordering.payments.stripe',
        label: 'Invoice Footer',
        description: 'Supports tags.',
        condition: 'Only appears when Create Invoice is on.',
        dependsOn: 'storefront.global.ordering.stripeCreateInvoice',
    },
    {
        id: 'storefront.global.ordering.stripeCollectPhoneNumber',
        area: 'storefront.global.ordering.payments.stripe',
        label: 'Collect Phone Number',
        description: "Collects the buyer's phone number on the Stripe checkout page.",
    },
    /*
     * The organization-wide appearance defaults. Everything from "Favicon" down lives on the
     * page’s own Legacy Settings tab: the product steers people to the per-storefront editors
     * instead, but the fields are still live and still what a support question lands on, so they
     * are linkable. The tab is selected by ?tab= on arrival.
     */
    {
        id: 'storefront.appearance.headerLogo',
        area: 'storefront.global.appearance',
        label: 'Header Logo',
        description: 'Organization-wide default logo shown in the storefront header.',
    },
    {
        id: 'storefront.appearance.headerLink',
        area: 'storefront.global.appearance',
        label: 'Header Link',
        description: 'URL the header logo links to.',
    },
    {
        id: 'storefront.appearance.useHeaderSearch',
        area: 'storefront.global.appearance',
        label: 'Default Use Header Search',
        description: 'Puts the search bar in the header instead of the main content area.',
    },
    {
        id: 'storefront.appearance.includeFooter',
        area: 'storefront.global.appearance',
        label: 'Default Include Footer',
        description: 'Default footer visibility used to seed newly created storefronts.',
    },
    {
        id: 'storefront.appearance.footerLogo',
        area: 'storefront.global.appearance',
        label: 'Footer Logo',
        description: 'Organization-wide default logo shown in the storefront footer.',
        condition: 'Only appears when Default Include Footer is on.',
        dependsOn: 'storefront.appearance.includeFooter',
    },
    {
        id: 'storefront.appearance.footerContactDetails',
        area: 'storefront.global.appearance',
        label: 'Footer Contact Details',
        description: 'Default footer contact details, used by storefronts that do not set their own.',
        condition: 'Only appears when Default Include Footer is on.',
        dependsOn: 'storefront.appearance.includeFooter',
    },
    {
        id: 'storefront.appearance.footerText',
        area: 'storefront.global.appearance',
        label: 'Footer Text',
        description: 'Default footer text, used by storefronts that do not set their own.',
        condition: 'Only appears when Default Include Footer is on.',
        dependsOn: 'storefront.appearance.includeFooter',
    },
    {
        id: 'storefront.appearance.footerPhone',
        area: 'storefront.global.appearance',
        label: 'Footer Phone',
        description: 'Default footer phone number, used by storefronts that do not set their own.',
        condition: 'Only appears when Default Include Footer is on.',
        dependsOn: 'storefront.appearance.includeFooter',
    },
    {
        id: 'storefront.appearance.footerEmail',
        area: 'storefront.global.appearance',
        label: 'Footer Email',
        description: 'Default footer email address, used by storefronts that do not set their own.',
        condition: 'Only appears when Default Include Footer is on.',
        dependsOn: 'storefront.appearance.includeFooter',
    },
    {
        id: 'storefront.appearance.footerFacebook',
        area: 'storefront.global.appearance',
        label: 'Facebook',
        description: 'Stores a handle; the server prepends the base URL.',
        condition: 'Only appears when Default Include Footer is on.',
        dependsOn: 'storefront.appearance.includeFooter',
    },
    {
        id: 'storefront.appearance.footerYouTube',
        area: 'storefront.global.appearance',
        label: 'YouTube',
        description: 'Stores a handle; the server prepends the base URL.',
        condition: 'Only appears when Default Include Footer is on.',
        dependsOn: 'storefront.appearance.includeFooter',
    },
    {
        id: 'storefront.appearance.footerLinkedIn',
        area: 'storefront.global.appearance',
        label: 'LinkedIn',
        description: 'Stores a handle; the server prepends the base URL.',
        condition: 'Only appears when Default Include Footer is on.',
        dependsOn: 'storefront.appearance.includeFooter',
    },
    {
        id: 'storefront.appearance.footerTwitter',
        area: 'storefront.global.appearance',
        label: 'Twitter',
        description: 'Stores a handle; the server prepends the base URL.',
        condition: 'Only appears when Default Include Footer is on.',
        dependsOn: 'storefront.appearance.includeFooter',
    },
    {
        id: 'storefront.appearance.footerX',
        area: 'storefront.global.appearance',
        label: 'X',
        description: 'Stores a handle; the server prepends the base URL.',
        condition: 'Only appears when Default Include Footer is on.',
        dependsOn: 'storefront.appearance.includeFooter',
    },
    {
        id: 'storefront.appearance.footerBluesky',
        area: 'storefront.global.appearance',
        label: 'Bluesky',
        description: 'Stores a handle; the server prepends the base URL.',
        condition: 'Only appears when Default Include Footer is on.',
        dependsOn: 'storefront.appearance.includeFooter',
    },
    {
        id: 'storefront.appearance.bannerHeight',
        area: 'storefront.global.appearance.banners',
        label: 'Banner Height',
        description: 'Default height for the banner container, so banners of differing sizes do not jump the page.',
    },
    {
        id: 'storefront.appearance.favicon',
        area: 'storefront.global.appearance.legacy',
        label: 'Favicon',
    },
    {
        id: 'storefront.appearance.name',
        area: 'storefront.global.appearance.legacy',
        label: 'Global Name',
        description: 'Default storefront name, used by storefronts that do not set their own.',
    },
    {
        id: 'storefront.appearance.title',
        area: 'storefront.global.appearance.legacy',
        label: 'Default Title',
        description: 'Default page title, used by storefronts that do not set their own.',
    },
    {
        id: 'storefront.appearance.bannerText',
        area: 'storefront.global.appearance.legacy',
        label: 'Default Banner Text',
        description: 'Default banner text, used by storefronts that do not set their own.',
    },
    /*
     * These four render only when Theme (StorefrontThemeSelection, just above them in the Legacy
     * tab) is set to Custom. Theme itself is not wrapped in <Setting> anywhere, so there is no
     * registered id for `dependsOn` to name - condition-only.
     */
    {
        id: 'storefront.appearance.menuBarColor',
        area: 'storefront.global.appearance.legacy',
        label: 'Menu Bar Color',
        condition: 'Only appears when Theme is set to Custom.',
    },
    {
        id: 'storefront.appearance.menuBarTextColor',
        area: 'storefront.global.appearance.legacy',
        label: 'Menu Bar Text Color',
        condition: 'Only appears when Theme is set to Custom.',
    },
    {
        id: 'storefront.appearance.backgroundColor',
        area: 'storefront.global.appearance.legacy',
        label: 'Background Color',
        condition: 'Only appears when Theme is set to Custom.',
    },
    {
        id: 'storefront.appearance.textColor',
        area: 'storefront.global.appearance.legacy',
        label: 'Text Color',
        condition: 'Only appears when Theme is set to Custom.',
    },
    {
        id: 'storefront.appearance.headerFont',
        area: 'storefront.global.appearance.legacy',
        label: 'Header Font',
        description: 'Default header font, used by storefronts that do not set their own.',
    },
    {
        id: 'storefront.appearance.defaultFont',
        area: 'storefront.global.appearance.legacy',
        label: 'Default Font',
        description:
            'Default font used throughout the storefront outside the header, used by storefronts that do not set their own.',
    },
    {
        id: 'storefront.appearance.maxWidth',
        area: 'storefront.global.appearance.legacy',
        label: 'Max Width',
        description: 'Default maximum content width in pixels, used by storefronts that do not set their own.',
    },
    {
        id: 'storefront.appearance.showCategories',
        area: 'storefront.global.appearance.legacy',
        label: 'Default Show Categories',
        description: 'Default value for showing categories on the home page, used to seed newly created storefronts.',
    },
    // ------------------------------------------- per-storefront Products (Settings tab)
    {
        id: 'storefront.products.defaultMarkup',
        area: 'storefront.products',
        label: 'Default Markup',
        description: 'Falls back to the organization-wide markup when empty.',
    },
    {
        id: 'storefront.products.defaultMarkupType',
        area: 'storefront.products',
        label: 'Markup Type',
        description: 'Falls back to the organization-wide markup type when empty.',
    },
    {
        id: 'storefront.products.ignoreInheritedProducts',
        area: 'storefront.products',
        label: 'Ignore Inherited Products',
        description:
            'Ignores products marked to appear on every storefront; only products explicitly included in this storefront show up.',
    },
    {
        id: 'storefront.products.honorVendorPromos',
        area: 'storefront.products',
        label: 'Honor Vendor Promos',
        description:
            "Passes a distributor's promotional cost through to the shopper at your usual markup. Prices move when the vendor runs a promotion, without any action from you.",
        // Its own render gate (canManagePromos) is narrower than the area's storefrontAdmin - see the
        // PRIVACY note on ConditionalSetting. This is what keeps it off the reference list too.
        permission: 'productPromosAdmin',
    },
    {
        id: 'storefront.products.honorProductPromos',
        area: 'storefront.products',
        label: 'Honor Product Promos',
        description:
            'Applies promos you create on products, which reduce the price out of your own margin. The shopper always gets whichever price is lower, and promos never stack with a vendor promotion.',
        permission: 'productPromosAdmin',
    },
    {
        id: 'storefront.products.vendorFilter',
        area: 'storefront.products',
        label: 'Storefront Vendor Filter',
        description: "Restricts this storefront's product listings to these vendors, overriding the organization-wide filter.",
    },
    {
        id: 'storefront.products.vendorFilterStrategy',
        area: 'storefront.products',
        label: 'Vendor Filter Strategy',
        description: 'Whether the Storefront Vendor Filter list above includes only those vendors or excludes them.',
    },
    {
        id: 'storefront.products.showLinkedItems',
        area: 'storefront.products',
        label: 'Show Linked Items on Storefront',
        description:
            "Whether products' linked items appear on this storefront's product pages, or inherits the organization-wide setting.",
    },
    {
        id: 'storefront.products.allowProductVideo',
        area: 'storefront.products',
        label: 'Allow Playing Product Video',
        description: 'Shows a play button on products with a video URL that opens a video player.',
    },
    {
        id: 'storefront.products.enableImageGallery',
        area: 'storefront.products',
        label: 'Enable Product Image Gallery',
        description: "Shows a gallery on the product details page so shoppers can scroll through all of a product's images.",
    },
    {
        id: 'storefront.products.enableProductTags',
        area: 'storefront.products',
        label: 'Enable Product Tags',
        description: 'Shows product tags as decorations on product cards and rows, and lets shoppers filter search results by tag.',
    },
    {
        id: 'storefront.products.enableFavorites',
        area: 'storefront.products',
        label: 'Enable Favorites',
        description:
            'Lets shoppers favorite products with a heart icon and filter search results down to their favorites. Requires the shopper to be signed in.',
    },
    {
        /*
         * Gated on entitlements.data.searchAutocompleteEnabled (StorefrontSearchBarEditor), which is
         * a raw org-entitlements flag with no backing CASL claim - there is no `permission` string to
         * put here, so `SettingsReferencePage`'s permission filter cannot hide this one from an
         * unentitled storefrontAdmin the way it hides the promo/recommendation toggles above.
         * Left unaddressed rather than half-solved: flagging it here so it isn't mistaken for covered.
         */
        id: 'storefront.products.buildSearchSuggestions',
        area: 'storefront.products',
        label: 'Build Search Suggestions',
        description:
            'Builds search suggestions nightly for this storefront from its catalog and shopper activity. Turning this on does not display them anywhere by itself; each search bar decides whether to show a suggestion panel, in the storefront designer.',
    },
    // ---------------------------- affiliations band (the logo strip on the home page)
    {
        id: 'storefront.affiliations.label',
        area: 'storefront.pages.affiliations',
        label: 'Band Label',
        description: 'The heading shown above the affiliation logos.',
    },
    {
        id: 'storefront.affiliations.description',
        area: 'storefront.pages.affiliations',
        label: 'Band Description',
        description: "Optional blurb shown under the band's label. Renders only when written.",
    },
    {
        id: 'storefront.affiliations.labelPosition',
        area: 'storefront.pages.affiliations',
        label: 'Label Position',
        description: 'Where the label sits relative to the logos: top, left, or bottom.',
    },
    {
        id: 'storefront.affiliations.pagePosition',
        area: 'storefront.pages.affiliations',
        label: 'Page Position',
        description: 'Where the band sits on the home page.',
    },
    {
        id: 'storefront.affiliations.alignment',
        area: 'storefront.pages.affiliations',
        label: 'Alignment',
        description: 'Horizontal alignment of the logos within the band.',
    },
    {
        id: 'storefront.affiliations.gap',
        area: 'storefront.pages.affiliations',
        label: 'Gap (px)',
        description: 'Space between logos.',
    },
    {
        id: 'storefront.affiliations.scroll',
        area: 'storefront.pages.affiliations',
        label: 'Scroll (marquee)',
        description: 'Scrolls the logos continuously instead of sitting still.',
    },
    // ---------------------------- product page editor (behavior fields, not styling)
    // Each of these lives behind its own toolbar panel (Description / Linked Items / Added
    // Together / More Like This), not the plain storefront.pages.product screen area — see
    // storefront.pages.product.* in areas.ts and the STATUS.md note on toolbar-opened editors.
    {
        id: 'storefront.productPage.descriptionCollapsible',
        area: 'storefront.pages.product.description',
        label: 'Collapsible',
        description: 'Collapses the product description behind a "Show more" toggle past a fixed height.',
    },
    {
        id: 'storefront.productPage.descriptionCollapsedHeight',
        area: 'storefront.pages.product.description',
        label: 'Collapsed height (px)',
        description: 'Height of the description before it collapses.',
        condition: 'Only appears when Collapsible is on.',
        dependsOn: 'storefront.productPage.descriptionCollapsible',
    },
    {
        id: 'storefront.productPage.linkedShowAddToCart',
        area: 'storefront.pages.product.linked',
        label: 'Show Add to Cart',
        description: 'Shows an Add to Cart button on each linked-item card.',
        condition: 'Only appears when Show Linked Items on Storefront is not Disabled.',
        dependsOn: 'storefront.products.showLinkedItems',
    },
    {
        id: 'storefront.productPage.enableFrequentlyAddedTogether',
        area: 'storefront.pages.product.addedTogether',
        label: 'Show Frequently Added Together',
        description: 'Shows a row of products frequently purchased alongside this one. Requires the product recommendations entitlement.',
        // Its own render gate (canRecommendations) is narrower than the area's storefrontAdmin.
        permission: 'productRecommendationsAdmin',
    },
    {
        id: 'storefront.productPage.enableMoreLikeThis',
        area: 'storefront.pages.product.moreLikeThis',
        label: 'Show More Like This',
        description: 'Shows a row of similar products below the product details. Requires the product recommendations entitlement.',
        permission: 'productRecommendationsAdmin',
    },
    // ---------------------------- search page editor (behavior fields, not styling)
    // Same deal: these sit behind the Container / Filters toolbar panels, not the plain
    // storefront.pages.search screen area.
    {
        id: 'storefront.searchPage.cardGap',
        area: 'storefront.pages.search.container',
        label: 'Card gap',
        description: 'Space between result cards or rows in the results grid.',
    },
    {
        id: 'storefront.searchPage.facetsEnabled',
        area: 'storefront.pages.search.filters',
        label: 'Facet filters',
        description: 'Shows the shopper facet filtering controls (manufacturer, attributes, and so on) on this page.',
    },
    {
        id: 'storefront.searchPage.facetLayout',
        area: 'storefront.pages.search.filters',
        label: 'Facet Layout',
        description: 'Whether facet filters render as a side panel or inline menus above the results.',
    },
    {
        id: 'storefront.searchPage.facetPriceFilter',
        area: 'storefront.pages.search.filters',
        label: 'Price range',
        description: 'Adds a price range filter to the facet filters.',
    },
] as const;

export type _StorefrontSettingsAreValid = CheckDescriptors<typeof STOREFRONT_SETTINGS>;
